import { useContext } from 'react';
import axios, { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import type { DynamicObjectInterface } from '../sdk/type';
import { LoadingContext, Theme } from '../ui/components';
import { getTranslatedMessage, randomStr } from '../ui/utils';
import { useAdminToast } from '../ui/components/AdminToast/AdminToast';

export const useAPICall = () => {
  const setLoadingStack = useContext(LoadingContext);
  const { adminToast } = useAdminToast();
  const { t } = useTranslation();

  const id = randomStr(4);
  let result;
  return async <T extends (...args: DynamicObjectInterface[]) => Promise<DynamicObjectInterface>>(
    apiFn: T,
    ...args: Parameters<T>
  ): Promise<ReturnType<T>> => {
    try {
      setLoadingStack?.(prev => [...prev, id]);
      result = await apiFn(...args);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;

        if (axiosError.response?.status === 500) {
          adminToast({
            message: getTranslatedMessage(t, t('dashboard.api-message.common.contactAdministrator')),
            theme: Theme.ERROR,
          });
        }

        result = {
          status: axiosError.response?.status,
          data: axiosError.response?.data,
        };
      } else {
        result = error;
      }
    } finally {
      setLoadingStack?.(prev => {
        let arr = [...prev];
        let idx = arr.indexOf(id);
        if (idx !== -1) arr.splice(idx, 1);
        return arr;
      });
    }
    return result;
  };
};
